import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Input } from "../components/ui/input"
import { cn } from "../lib/utils"
import { Search } from "lucide-react"

export function InputRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const placeholderRaw = element.placeholder ?? (element as any).hint
  const placeholder = placeholderRaw
    ? String(resolveSymbol(placeholderRaw) ?? "")
    : undefined

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatchEvent(element.id, "change", { value: e.target.value })
  }

  // Support bind for controlled value — wrap in #{} to resolve as state path
  const boundValue = element.bind
    ? String(resolveSymbol(`#{${element.bind}}`) ?? "")
    : undefined

  const inputType = (element.inputType as string) ?? "text"
  const required = element.required === true || element.required === "true"
  const disabled = element.disabled === true || element.disabled === "true"
  const readOnly = element.readonly === true
  const pattern = element.pattern as string | undefined
  const min = element.min as number | undefined
  const max = element.max as number | undefined
  const step = element.step as number | undefined

  const inputProps = {
    type: inputType,
    placeholder,
    required: required || undefined,
    disabled: disabled || undefined,
    readOnly: readOnly || undefined,
    pattern: pattern || undefined,
    min: min,
    max: max,
    step: step,
    ...(boundValue !== undefined ? { value: boundValue } : {}),
    onChange: handleChange,
    "data-testid": `input-${element.id}`,
  }

  // Render with leading icon (e.g., search)
  if (element.icon === "search") {
    return (
      <div className={cn("relative w-full sm:w-64 flex-shrink-0", element.className)}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-9"
          {...inputProps}
        />
      </div>
    )
  }

  return (
    <Input
      className={element.className}
      {...inputProps}
    />
  )
}
